import React, { FC } from 'react';

import { mqMin, LucidTheme } from '@lucid/theme';
import { keyframes } from '@emotion/react';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Image from '../image/image';

export interface CarouselLogoProps {
  /** The text displayed above all the logos. */
  header?: string | null;
  /** The logos to be shown on the Carousel. */
  logos?: {
    imageLink: string;
    altText: string;
  }[];
}

const horizontalScroll = keyframes({
  from: {
    transfrom: 'translateX(0)'
  },
  to: {
    transform: 'translateX(calc(((150px * 6) + 40px) * -1))'
  }
});
const horizontalScroll1 = keyframes({
  from: {
    transform: 'translateX(calc((150px * 6) + 40px))'
  },
  to: {
    // transform: 'translateX(calc((150px * 3) + 40px))'
    transform: 'translateX(0)'
  }
});

const CarouselLogo: FC<CarouselLogoProps> = ({ header = '', logos = [] }) => {
  return (
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        backgroundColor: theme.colors.coolGrey[0],
        paddingTop: theme.space.s64,
        paddingBottom: theme.space.s64
      })}
    >
      <Container
        css={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <Box>
          <Heading
            as="h2"
            color="secondary"
            variant="headline2XL"
          >
            <span
              css={(theme: LucidTheme) => ({
                backgroundImage: `linear-gradient(0, ${theme.colors.orange[10]} 0, ${theme.colors.orange[10]})`,
                backgroundSize: '100% 17px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '0 100%'
              })}
            >
              {header}
            </span>
          </Heading>
        </Box>
        <Box
          css={(theme: LucidTheme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 150px)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gridGap: theme.space.s40,
            position: 'relative',
            overflow: 'hidden',
            width: 'calc(100% + 3.75rem)',
            transform: 'translate3d(0, 0, 0)',
            padding: `0 ${theme.space.s32}px`,
            [mqMin.tablet]: {
              left: 'unset',
              width: '100%',
              justifyContent: 'center'
            }
          })}
        >
          {logos.map((logo, index) => (
            <Image
              key={`image-${index}`}
              alt={logo.altText}
              css={{
                position: 'relative',
                width: '100%',
                animation: `${horizontalScroll} 15s linear infinite`,
                [mqMin.tablet]: {
                  animation: 'unset'
                },
                '& > .image': {
                  objectFit: 'contain',
                  height: '100%',
                  maxHeight: '50px'
                }
              }}
              src={logo.imageLink}
            />
          ))}
        </Box>
        <Box
          css={(theme: LucidTheme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 150px)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gridGap: theme.space.s40,
            position: 'absolute',
            overflow: 'hidden',
            bottom: 0,
            left: 0,
            width: 'calc(100% + 3.75rem)',
            transform: 'translate3d(0, 0, 0)',
            padding: `0 ${theme.space.s32}px`,
            [mqMin.tablet]: {
              display: 'none',
              visibility: 'hidden',
              left: 'unset',
              width: '100%',
              justifyContent: 'center'
            }
          })}
        >
          {logos.map((logo, index) => (
            <Image
              key={`image-${index}`}
              alt={logo.altText}
              css={{
                position: 'relative',
                width: '100%',
                animation: `${horizontalScroll1} 15s linear infinite`,
                [mqMin.tablet]: {
                  animation: 'unset'
                },
                '& > .image': {
                  objectFit: 'contain',
                  height: '100%',
                  maxHeight: '50px'
                }
              }}
              src={logo.imageLink}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export { CarouselLogo };
export default CarouselLogo;
